import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import { Bookmark, FilePlus, FileText, FolderClosed, FolderKanban, FolderPlus, Inbox, LayoutDashboard, ListPlus, Play, SquareKanban, SquarePlus, Star, Zap } from 'lucide-react';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { Highlighted, useDebounced } from './dashboard/Search';
import { searchLink, type SearchResult } from './dashboard/searchText';
import { TypeIcon } from './tracker/fields';
import { useFilters, useProjects, type IssueType } from './tracker/model';
import { openCreate, useHere, useNewBoard } from './spaces/dialogs';
import { useNewPage } from '../docs/model';
import { track } from '../lib/track';

/** `GET /api/find` (crates/server/src/api/find.rs). */
export interface FindIssue {
  id: string;
  key: string;
  title: string;
  type: IssueType;
  status: string;
  category: 'todo' | 'doing' | 'done';
  projectId: string;
  /** Found only by a comment, not by the key, title or description. */
  matchedIn?: 'comment';
}
export interface FindResult {
  issues: FindIssue[];
  boards: SearchResult[];
  projects: { id: string; key: string; name: string }[];
  /** Spaces of the person's teams (crates/server/src/api/find.rs). */
  spaces?: { id: string; name: string; teamId: string; teamName: string }[];
  /** Pages of the documents, with a snippet where the words are (U+E000…U+E001 mark them). */
  pages?: { id: string; title: string; icon: string | null; spaceId: string; spaceName: string; snippet: string | null }[];
  tookMs: number;
}

/** Search across the workspace while typing (debounced; keeps the last results until new ones arrive). */
export function useFind(q: string, enabled = true) {
  const term = useDebounced(q.trim(), 140);
  return useQuery({
    queryKey: ['find', term],
    queryFn: () => api.get<FindResult>(`/find?q=${encodeURIComponent(term)}`),
    enabled: enabled && term.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
}

export interface FindEntry {
  key: string;
  group: string;
  icon: ReactNode;
  label: ReactNode;
  hint?: ReactNode;
  to: string;
  /** An action instead of a link (create something). */
  run?: () => void;
}

/** Results of `useFind` as palette entries, grouped: issues, boards, projects, spaces. */
export function findEntries(data: FindResult | undefined, t: (k: string) => string): FindEntry[] {
  if (!data) return [];
  return [
    ...data.issues.map((i) => ({
      key: `issue-${i.id}`,
      group: t('quickOpen.issues'),
      icon: <TypeIcon type={i.type} size={16} />,
      label: (
        <>
          <span className="qo-key">{i.key}</span>
          <span className={i.category === 'done' ? 'qo-done' : undefined}>{i.title}</span>
        </>
      ),
      hint: (
        <span className="hint">
          {i.status}
          {i.matchedIn === 'comment' && ` · ${t('quickOpen.inComment')}`}
        </span>
      ),
      to: `/t/${i.projectId}?i=${encodeURIComponent(i.key)}`,
    })),
    ...data.boards.map((r) => ({
      key: `board-${r.board.id}`,
      group: t('quickOpen.boards'),
      icon: <LayoutDashboard size={16} />,
      label: r.title ? <Highlighted text={r.title} /> : r.board.title || t('common.untitled'),
      hint: r.snippet ? (
        <span className="hint qo-snippet">
          <Highlighted text={r.snippet} />
        </span>
      ) : undefined,
      to: searchLink(r),
    })),
    ...data.projects.map((p) => ({
      key: `project-${p.id}`,
      group: t('quickOpen.projects'),
      icon: <FolderKanban size={16} />,
      label: (
        <>
          <span className="qo-key">{p.key}</span>
          {p.name}
        </>
      ),
      to: `/t/${p.id}`,
    })),
    ...(data.pages ?? []).map((p) => ({
      key: `page-${p.id}`,
      group: t('quickOpen.pages'),
      icon: <FileText size={16} />,
      label: p.title || t('docs.untitled'),
      hint: p.snippet ? (
        <span className="hint qo-snippet">
          <Highlighted text={p.snippet} />
        </span>
      ) : (
        <span className="hint">{p.spaceName}</span>
      ),
      to: `/d/${p.id}`,
    })),
    ...(data.spaces ?? []).map((sp) => ({
      key: `space-${sp.id}`,
      group: t('quickOpen.spaces'),
      icon: <FolderClosed size={16} />,
      label: sp.name,
      hint: sp.teamName,
      to: `/s/${sp.id}`,
    })),
  ];
}

const useQuickOpen = create<{ open: boolean; query: string }>(() => ({ open: false, query: '' }));
/** Opens ⌘K, with a search already typed (a page's label). */
export const openQuickOpen = (query = '') => useQuickOpen.setState({ open: true, query });

/**
 * ⌘K outside boards: find any issue, board or project, or jump to a section. On a board, the
 * board's own palette (board/ui/CommandPalette) shows the same results under its actions.
 */
export function QuickOpen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = useQuickOpen((s) => s.open);
  const close = () => useQuickOpen.setState({ open: false });
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const found = useFind(q, open);
  const session = useSession();
  const here = useHere();
  const projects = useProjects();
  const filters = useFilters(here.org, open);
  const newBoard = useNewBoard(session.data!);
  const newPage = useNewPage();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'л') && !e.altKey) {
        e.preventDefault();
        useQuickOpen.setState((s) => ({ open: !s.open, query: '' }));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    if (open) {
      setQ(useQuickOpen.getState().query);
      setActive(0);
    }
  }, [open]);

  const entries = useMemo<FindEntry[]>(() => {
    const needle = q.trim().toLowerCase();
    const go: FindEntry[] = [
      { key: 'go-all', group: t('quickOpen.goTo'), icon: <LayoutDashboard size={16} />, label: t(here.org ? 'nav.everything' : 'nav.everythingPersonal'), to: here.org ? `/?org=${here.org}` : '/' },
      { key: 'go-tasks', group: t('quickOpen.goTo'), icon: <SquareKanban size={16} />, label: t('tracker.myIssues'), to: '/t' },
      { key: 'go-inbox', group: t('quickOpen.goTo'), icon: <Inbox size={16} />, label: t('inbox.title'), to: '/inbox' },
      { key: 'go-filters', group: t('quickOpen.goTo'), icon: <Bookmark size={16} />, label: t('tracker.filters.title'), to: '/t?f=filters' },
      // Starred and recent boards and projects: the menu has favorites, the rest is here.
      { key: 'go-recent', group: t('quickOpen.goTo'), icon: <Star size={16} />, label: t('nav.starredRecent'), to: here.org ? `/?org=${here.org}&view=recent` : '/?view=recent' },
    ].filter((e) => !needle || String(e.label).toLowerCase().includes(needle));
    // Saved filters by name: the menu no longer lists them.
    const saved: FindEntry[] = needle
      ? (filters.data ?? [])
          .filter((f) => f.name.toLowerCase().includes(needle))
          .slice(0, 8)
          .map((f) => ({ key: `filter-${f.id}`, group: t('tracker.filters.title'), icon: <Bookmark size={16} />, label: f.name, hint: f.projectKey ? <span className="hint">{f.projectKey}</span> : undefined, to: `/t/f/${f.id}` }))
      : [];
    // Create here: in the project, space and team of the page, else the dialog asks.
    const project = projects.data?.find((p) => p.id === here.project);
    const inProject = project ? <span className="hint">{project.key}</span> : undefined;
    const where = { org: here.org, team: here.team, space: here.space, project: here.project };
    const group = t('create.title');
    const actions: FindEntry[] = [
      { key: 'new-board', group, icon: <LayoutDashboard size={16} />, label: t('create.board'), to: '', run: () => void newBoard(where) },
      {
        key: 'new-page',
        group,
        icon: <FilePlus size={16} />,
        label: t('create.page'),
        to: '',
        run: () => {
          const team = session.data?.teams.find((tm) => (here.org ? tm.orgId === here.org : tm.orgId === null)) ?? session.data?.teams[0];
          void newPage(here.space ? { spaceId: here.space } : { teamId: here.team ?? team?.id });
        },
      },
      { key: 'new-issue', group, icon: <SquarePlus size={16} />, label: t('create.issue'), hint: inProject, to: '', run: () => openCreate('issue', where) },
      { key: 'new-sprint', group, icon: <Zap size={16} />, label: t('create.sprint'), hint: inProject, to: '', run: () => openCreate('sprint', where) },
      { key: 'start-sprint', group, icon: <Play size={16} />, label: t('create.startSprint'), hint: inProject, to: '', run: () => openCreate('startSprint', where) },
      { key: 'new-project', group, icon: <ListPlus size={16} />, label: t('create.project'), to: '', run: () => openCreate('project', where) },
      { key: 'new-space', group, icon: <FolderPlus size={16} />, label: t('create.space'), to: '', run: () => openCreate('space', where) },
    ].filter((e) => !needle || `${group} ${e.label}`.toLowerCase().includes(needle));
    return needle.length >= 2 ? [...findEntries(found.data, t), ...saved, ...actions, ...go] : [...saved, ...go, ...actions];
  }, [q, found.data, t, here, projects.data, filters.data, newBoard, newPage, session.data]);

  useEffect(() => setActive((a) => Math.min(a, Math.max(0, entries.length - 1))), [entries.length]);
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;
  const exec = (e: FindEntry | undefined) => {
    if (!e) return;
    track('quick_open_used', { kind: e.run ? e.key : e.to.startsWith('/b/') ? 'board' : e.to.startsWith('/t/i/') ? 'issue' : e.to.startsWith('/d/') ? 'page' : 'project', query_chars: q.trim().length });
    close();
    if (e.run) e.run();
    else navigate(e.to);
  };
  const searching = q.trim().length >= 2 && found.isFetching && !found.data;

  let lastGroup = '';
  return (
    <>
      <div className="overlay" style={{ background: 'rgba(10,11,14,0.2)', backdropFilter: 'none' }} onPointerDown={close} />
      <div className="palette" role="dialog" aria-label={t('quickOpen.title')}>
        <input
          autoFocus
          value={q}
          placeholder={t('quickOpen.placeholder')}
          onChange={(ev) => {
            setQ(ev.target.value);
            setActive(0);
          }}
          onKeyDown={(ev) => {
            if (ev.key === 'Escape') close();
            else if (ev.key === 'ArrowDown') {
              ev.preventDefault();
              setActive((a) => Math.min(entries.length - 1, a + 1));
            } else if (ev.key === 'ArrowUp') {
              ev.preventDefault();
              setActive((a) => Math.max(0, a - 1));
            } else if (ev.key === 'Enter') {
              ev.preventDefault();
              exec(entries[active]);
            }
          }}
        />
        <div className="palette-list" ref={listRef}>
          {entries.length === 0 && <div className="empty-state" style={{ padding: 24 }}>{searching ? t('common.loading') : t('palette.noResults')}</div>}
          {entries.map((en, i) => {
            const header = en.group !== lastGroup ? en.group : null;
            lastGroup = en.group;
            return (
              <div key={en.key}>
                {header && <div className="menu-label">{header}</div>}
                <div className="palette-item" data-active={i === active} onMouseMove={() => setActive(i)} onClick={() => exec(en)}>
                  {en.icon}
                  <span className="qo-label">{en.label}</span>
                  {en.hint}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
